import {
  AlertTriangle,
  ChevronRight,
  ClipboardList,
  FileText,
  Home,
  Mail,
  Shield,
  UserCheck,
  Users,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'

interface SidebarProps {
  isCollapsed: boolean
  onClose?: () => void
}

interface NavItem {
  title: string
  href: string
  icon: React.ElementType
  children?: NavItem[]
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/',
    icon: Home,
  },
  {
    title: 'Candidates',
    href: '/candidates',
    icon: Users,
  },
  {
    title: 'References',
    href: '/references',
    icon: FileText,
  },
]

const adminNavItems: NavItem[] = [
  {
    title: 'Admin',
    href: '/admin',
    icon: Shield,
    children: [
      {
        title: 'Users',
        href: '/admin/users',
        icon: Users,
      },
      {
        title: 'Workspaces',
        href: '/admin/workspaces',
        icon: Home,
      },
      {
        title: 'Templates',
        href: '/admin/templates',
        icon: ClipboardList,
      },
      {
        title: 'Email Templates',
        href: '/admin/email-templates',
        icon: Mail,
      },
      {
        title: 'Fraud Detection',
        href: '/admin/fraud-logs',
        icon: AlertTriangle,
      },
      {
        title: 'Audit Logs',
        href: '/admin/audit-logs',
        icon: FileText,
      },
    ],
  },
]

export function Sidebar({ isCollapsed, onClose }: SidebarProps) {
  const location = useLocation()
  const { user } = useAuth()
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const year = new Date().getFullYear()
  const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const toggleSection = (sectionHref: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionHref)
        ? prev.filter(href => href !== sectionHref)
        : [...prev, sectionHref],
    )
  }

  const isSectionExpanded = (href: string) => {
    return expandedSections.includes(href) || isActive(href)
  }

  return (
    <div className="flex flex-col h-full bg-sidebar-bg text-sidebar-text">
      {/* Logo / Brand */}
      <div className="flex items-center h-16 px-4 border-b border-sidebar-border">
        {!isCollapsed && (
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold text-sidebar-text-hover"
          >
            <UserCheck className="w-6 h-6 text-primary" />
            <span>ReferEase</span>
          </Link>
        )}
        {isCollapsed && (
          <Link to="/" className="mx-auto">
            <UserCheck className="w-6 h-6 text-primary" />
          </Link>
        )}
        {onClose && (
          <Button variant="ghost" size="icon" className="ml-auto lg:hidden" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-4">
        <div className="py-4 space-y-1">
          {navItems.map(item => (
            <div key={item.href}>
              <Link to={item.href}>
                <Button
                  variant="ghost"
                  className={cn(
                    'w-full justify-start text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-hover transition-colors',
                    isActive(item.href)
                    && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                    isCollapsed && 'justify-center px-4',
                  )}
                >
                  <item.icon className={cn('h-5 w-5', !isCollapsed && 'mr-4')} />
                  {!isCollapsed && <span>{item.title}</span>}
                </Button>
              </Link>
              {/* Render children if expanded and parent is active */}
              {!isCollapsed && item.children && isActive(item.href) && (
                <div className="mt-4 ml-4 space-y-1">
                  {item.children.map(child => (
                    <Link key={child.href} to={child.href}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          'justify-start w-full text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-hover transition-colors',
                          isActive(child.href)
                          && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                        )}
                      >
                        <child.icon className="w-4 h-4 mr-4" />
                        <span>{child.title}</span>
                      </Button>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Admin Section - Only visible to admin users */}
          {user?.role === 'admin'
            && adminNavItems.map(item => (
              <div key={item.href}>
                {isCollapsed
                  ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className={cn(
                              'w-full justify-start text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-hover transition-colors',
                              isActive(item.href)
                              && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                              'justify-center px-4',
                            )}
                          >
                            <item.icon className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start">
                          {/* Optional direct link to Admin overview */}
                          <DropdownMenuItem asChild>
                            <Link to="/admin">
                              <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                <span>Admin Overview</span>
                              </div>
                            </Link>
                          </DropdownMenuItem>

                          {item.children?.map(child => (
                            <DropdownMenuItem key={child.href} asChild>
                              <Link to={child.href}>
                                <div className="flex items-center gap-2">
                                  <child.icon className="w-4 h-4" />
                                  <span>{child.title}</span>
                                </div>
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
                  : (
                      <div
                        onMouseEnter={() => toggleSection(item.href)}
                        onMouseLeave={() => toggleSection(item.href)}
                      >
                        <Button
                          variant="ghost"
                          className={cn(
                            'w-full justify-start text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-hover transition-colors',
                            isActive(item.href)
                            && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                            isCollapsed && 'justify-center px-4',
                          )}
                        >
                          <item.icon className={cn('h-5 w-5', !isCollapsed && 'mr-4')} />
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-left">{item.title}</span>
                              {item.children && (
                                <ChevronRight
                                  className={cn(
                                    'h-4 w-4 transition-transform',
                                    isSectionExpanded(item.href) && 'rotate-90',
                                  )}
                                />
                              )}
                            </>
                          )}
                        </Button>
                        {/* Render children if expanded */}
                        {!isCollapsed && item.children && isSectionExpanded(item.href) && (
                          <div className="mt-4 ml-4 space-y-1">
                            {item.children.map(child => (
                              <Link key={child.href} to={child.href}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={cn(
                                    'justify-start w-full text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-hover transition-colors',
                                    isActive(child.href)
                                    && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                                  )}
                                >
                                  <child.icon className="w-4 h-4 mr-4" />
                                  <span>{child.title}</span>
                                </Button>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
              </div>
            ))}
        </div>
      </ScrollArea>
      {/* Footer: Copyright and Version */}
      <div className="border-t border-sidebar-border px-4 py-3 flex flex-col items-center gap-2">
        {isCollapsed
          ? (
              <div className="w-full text-center text-[10px] text-sidebar-text/40">
                v
                {version}
              </div>
            )
          : (
              <div className="flex flex-col items-start gap-1">
                <div className="text-xs text-sidebar-text/70 flex items-center gap-1">
                  <UserCheck className="text-primary w-4 h-4" />
                  ReferEase &copy;
                  {' '}
                  {' '}
                  {year}
                </div>
                <div className="text-[10px] text-sidebar-text/40 text-center w-full">
                  v
                  {version}
                </div>
              </div>
            )}
      </div>
    </div>
  )
}
